/** 電話帳同期(CS1)の問い合わせと書き込み。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import type { ContactBookInput, ContactMode, ContactRole, ContactsSetting } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useIdpDownload } from "./hooks";

const KEY = ["idp", "contacts"] as const;
/** 実行中の履歴があるあいだ読み直す間隔 */
export const CONTACT_POLL_MS = 3000;

function useWrite<TArgs, TOut>(fn: (args: TArgs) => Promise<TOut>, label: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      void message.success(`${label}した`);
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

export function useContactsSetting(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, "setting"], queryFn: api.contactsSetting, enabled });
}

export function useSaveContactsSetting() {
  const api = useApi();
  return useWrite((data: Partial<ContactsSetting>) => api.saveContactsSetting(data), "設定を保存");
}

export function useTestContacts() {
  const api = useApi();
  return useMutation({
    mutationFn: api.testContacts,
    onSuccess: (r) =>
      r.ok ? void message.success(`接続できた: ${r.detail}`) : void message.error(r.detail),
    onError: (e) => void message.error(errorText(e)),
  });
}

/** v1 は電話帳 1 冊。初回の読み取りで(無効のまま)作られる。 */
export function useContactBook() {
  const api = useApi();
  const q = useQuery({ queryKey: [...KEY, "books"], queryFn: api.contactBooks });
  return { ...q, book: q.data?.[0] };
}

export function useSaveContactBook() {
  const api = useApi();
  return useWrite(
    (args: { id: number; data: ContactBookInput }) => api.saveContactBook(args.id, args.data),
    "電話帳を保存",
  );
}

export function useSetContactMembers() {
  const api = useApi();
  return useWrite(
    (args: {
      id: number;
      employee_ids: number[];
      roles: ContactRole[];
      mode: ContactMode | null;
    }) =>
      api.setContactMembers(args.id, {
        employee_ids: args.employee_ids,
        roles: args.roles,
        mode: args.mode,
      }),
    "個別指定を更新",
  );
}

export function useContactGroups() {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, "groups"], queryFn: api.contactGroups });
}

/** プレビュー。電話番号が出るので operator 以上だけ(API も同じ)。 */
export function useContactPreview(id: number | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, "preview", id],
    queryFn: () => api.contactPreview(id as number),
    enabled: enabled && id !== undefined,
  });
}

export function useContactPreviewCsv(id: number | undefined) {
  const download = useIdpDownload();
  return useCallback(() => {
    if (id !== undefined) void download(`/api/idp/contacts/books/${id}/preview.csv`);
  }, [download, id]);
}

export function useSyncContacts() {
  const api = useApi();
  return useWrite((id: number) => api.syncContacts(id), "同期を開始");
}

/** 撤収の記録(operator 以上。API も同じ)。 */
export function useContactWithdrawals(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, "withdrawals"],
    queryFn: api.contactWithdrawals,
    enabled,
  });
}

export function useCancelContactWithdrawal() {
  const api = useApi();
  return useWrite((employeeId: number) => api.cancelContactWithdrawal(employeeId), "取り消し");
}

export function useContactRuns(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, "runs", id],
    queryFn: () => api.contactRuns(id as number),
    enabled: id !== undefined,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === "running") ? CONTACT_POLL_MS : false,
  });
}
